import React, { useRef, useEffect } from 'react';
import {
  Plus, Trash2, GripVertical, ImageIcon, Film as FilmIcon,
  ChevronUp, ChevronDown, Scissors, Info, Layers, Loader2, Upload,
  Clock, Check, X as XIcon, Settings2, AlertCircle,
  Type, Heading, Quote, MousePointerClick, BarChart3, Image as ImageLucide,
} from 'lucide-react';
import type { StoryPartData, StorySlideTemplate } from './types';
import {
  STORY_MAX_SLIDES,
  STORY_SLIDE_DEFAULT_DURATION,
  STORY_IMAGE_RECOMMENDED_SIZE,
  STORY_MAX_DURATION_SECONDS,
  STORY_SUPPORTED_IMAGE_FORMATS,
  STORY_SUPPORTED_VIDEO_FORMATS,
  STORY_SLIDE_TEMPLATES,
  getSlideTemplateConfig,
} from './ReelsStoryConstants';

// ─── Template icon mapping ────────────────────────────────────────────

const TEMPLATE_ICONS: Record<string, React.ElementType> = {
  Type: Type,
  Heading: Heading,
  Quote: Quote,
  MousePointerClick: MousePointerClick,
  BarChart3: BarChart3,
  Image: ImageLucide,
};

function getTemplateIcon(iconName: string): React.ElementType {
  return TEMPLATE_ICONS[iconName] || Type;
}

// ─── Video Segment Preview Component ──────────────────────────────────

interface VideoSegmentPreviewProps {
  src: string;
  startSeconds?: number;
  endSeconds?: number;
}

const VideoSegmentPreview: React.FC<VideoSegmentPreviewProps> = ({
  src,
  startSeconds,
  endSeconds,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  const start = startSeconds ?? 0;
  const end = endSeconds ?? Infinity;
  const hasSegment = startSeconds !== undefined && endSeconds !== undefined;

  const formatTime = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  // Auto-pause at segment end
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !hasSegment) return;

    const handleTimeUpdate = () => {
      if (video.currentTime >= end) {
        video.pause();
        video.currentTime = start;
      }
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    return () => video.removeEventListener('timeupdate', handleTimeUpdate);
  }, [start, end, hasSegment]);

  // Build the video src with media fragment
  const videoSrc = hasSegment ? `${src}#t=${start},${end}` : src;

  return (
    <div className="relative" style={{ aspectRatio: '9/16', maxHeight: '280px' }}>
      <video
        ref={videoRef}
        src={videoSrc}
        className="w-full h-full object-contain bg-black rounded-lg"
        preload="metadata"
        playsInline
        controls
      />

      {/* Segment time badge */}
      {hasSegment && (
        <div className="absolute top-1.5 left-1.5 z-10">
          <span className="bg-black/70 text-white text-[10px] px-1.5 py-0.5 rounded flex items-center gap-1">
            <FilmIcon className="w-3 h-3" />
            {`${formatTime(start)} – ${formatTime(end)}`}
          </span>
        </div>
      )}
    </div>
  );
};

// ─── Slide Template Selector ──────────────────────────────────────────

interface SlideTemplateSelectorProps {
  template?: StorySlideTemplate;
  onChange: (template: StorySlideTemplate) => void;
}

const SlideTemplateSelector: React.FC<SlideTemplateSelectorProps> = ({
  template = 'text',
  onChange,
}) => {
  return (
    <div className="flex items-center gap-1 mb-2">
      {STORY_SLIDE_TEMPLATES.map((tmpl) => {
        const Icon = getTemplateIcon(tmpl.iconName);
        const isActive = tmpl.id === template;
        return (
          <button
            key={tmpl.id}
            type="button"
            onClick={() => onChange(tmpl.id)}
            title={`${tmpl.label} — ${tmpl.description}`}
            className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium transition-all ${
              isActive
                ? `${tmpl.badgeColor} ring-1 ring-current shadow-sm`
                : 'bg-gray-50 text-gray-400 hover:bg-gray-100 hover:text-gray-600'
            }`}
          >
            <Icon className="w-3 h-3" />
            <span className="hidden sm:inline">{tmpl.label}</span>
          </button>
        );
      })}
    </div>
  );
};

// ─── Story Part Editor ────────────────────────────────────────────────

interface StoryPartEditorProps {
  parts: StoryPartData[];
  onAddPart: () => void;
  onRemovePart: (index: number) => void;
  onUpdatePart: (index: number, updates: Partial<StoryPartData>) => void;
  onMovePart: (fromIndex: number, toIndex: number) => void;
  onAutoSplit: () => void;
  onPartMediaChange: (index: number, e: React.ChangeEvent<HTMLInputElement>) => void;
  onVideoAutoSplit: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isUploading: boolean;
  isVideoSplitting: boolean;
  autoSplitAvailable: boolean;
  videoSplitInfo: {
    videoUrl: string;
    totalDuration: number;
    segmentDuration: number;
    fileName: string;
  } | null;
  onSetSegmentDuration: (seconds: number) => void;
  onConfirmVideoSplit: () => void;
  onCancelVideoSplit: () => void;
}

export const StoryPartEditor: React.FC<StoryPartEditorProps> = ({
  parts,
  onAddPart,
  onRemovePart,
  onUpdatePart,
  onMovePart,
  onAutoSplit,
  onPartMediaChange,
  onVideoAutoSplit,
  isUploading,
  isVideoSplitting,
  autoSplitAvailable,
  videoSplitInfo,
  onSetSegmentDuration,
  onConfirmVideoSplit,
  onCancelVideoSplit,
}) => {
  const fileInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const videoSplitInputRef = useRef<HTMLInputElement | null>(null);

  const setFileInputRef = (index: number, el: HTMLInputElement | null) => {
    fileInputRefs.current[index] = el;
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700 flex items-center gap-2">
          <Layers className="w-4 h-4 text-pink-600" />
          <span>Slajdy historii</span>
          <span className="text-xs text-gray-400">({parts.length}/{STORY_MAX_SLIDES})</span>
        </label>
        <div className="flex gap-2">
          {autoSplitAvailable && (
            <button
              type="button"
              onClick={onAutoSplit}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-orange-700 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors"
            >
              <Scissors className="w-3.5 h-3.5" />
              Auto-podział tekstu
            </button>
          )}
          <button
            type="button"
            onClick={() => videoSplitInputRef.current?.click()}
            disabled={isUploading || isVideoSplitting}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-violet-700 bg-violet-50 border border-violet-200 rounded-lg hover:bg-violet-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isVideoSplitting ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Upload className="w-3.5 h-3.5" />
            )}
            {isVideoSplitting ? 'Dzielenie...' : 'Wideo → slajdy'}
          </button>
          <input
            ref={videoSplitInputRef}
            type="file"
            accept="video/mp4,video/quicktime,video/mov"
            onChange={onVideoAutoSplit}
            className="hidden"
          />
          <button
            type="button"
            onClick={onAddPart}
            disabled={parts.length >= STORY_MAX_SLIDES || isUploading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-pink-700 bg-pink-50 border border-pink-200 rounded-lg hover:bg-pink-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-3.5 h-3.5" />
            Dodaj slajd
          </button>
        </div>
      </div>

      {/* Video Split Configuration Panel */}
      {videoSplitInfo && (
        <div className="rounded-xl border-2 border-violet-300 bg-gradient-to-r from-violet-50 to-purple-50 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Settings2 className="w-4 h-4 text-violet-600" />
              <span className="text-sm font-semibold text-violet-800">Konfiguracja podziału wideo</span>
            </div>
            <button
              type="button"
              onClick={onCancelVideoSplit}
              className="w-6 h-6 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center transition-colors"
            >
              <XIcon className="w-3.5 h-3.5 text-gray-600" />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3 text-xs">
            <div className="bg-white rounded-lg p-2.5 border border-violet-100">
              <span className="text-gray-500 block mb-0.5">Plik</span>
              <span className="font-medium text-gray-800 truncate block">{videoSplitInfo.fileName}</span>
            </div>
            <div className="bg-white rounded-lg p-2.5 border border-violet-100">
              <span className="text-gray-500 block mb-0.5">Całkowity czas</span>
              <span className="font-medium text-gray-800">
                {Math.floor(videoSplitInfo.totalDuration / 60)}:{(videoSplitInfo.totalDuration % 60).toString().padStart(2, '0')}
              </span>
            </div>
          </div>

          <div className="bg-white rounded-lg p-3 border border-violet-100 space-y-2">
            <label className="flex items-center gap-2 text-xs font-medium text-gray-700">
              <Clock className="w-3.5 h-3.5 text-violet-500" />
              Długość segmentu (sekundy)
            </label>
            <div className="flex items-center gap-2">
              <input
                type="range"
                min={5}
                max={STORY_MAX_DURATION_SECONDS}
                step={5}
                value={videoSplitInfo.segmentDuration}
                onChange={(e) => onSetSegmentDuration(Number(e.target.value))}
                className="flex-1 h-2 rounded-lg appearance-none bg-violet-200 accent-violet-600"
              />
              <input
                type="number"
                min={5}
                max={STORY_MAX_DURATION_SECONDS}
                value={videoSplitInfo.segmentDuration}
                onChange={(e) => onSetSegmentDuration(Number(e.target.value))}
                className="w-16 text-center text-sm border border-violet-200 rounded-lg py-1 focus:ring-1 focus:ring-violet-400 focus:border-violet-400"
              />
              <span className="text-xs text-gray-500">s</span>
            </div>
          </div>

          {/* Preview of the split */}
          {(() => {
            const segCount = Math.ceil(videoSplitInfo.totalDuration / videoSplitInfo.segmentDuration);
            const capped = Math.min(segCount, STORY_MAX_SLIDES);
            const lastDuration = videoSplitInfo.totalDuration - (capped - 1) * videoSplitInfo.segmentDuration;
            return (
              <div className="bg-white rounded-lg p-3 border border-violet-100">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-700">Podgląd podziału</span>
                  <span className="text-xs text-violet-600 font-bold">{capped} slajdów</span>
                </div>
                <div className="flex gap-1 flex-wrap">
                  {Array.from({ length: capped }).map((_, i) => {
                    const isLast = i === capped - 1;
                    const dur = isLast ? Math.round(lastDuration) : videoSplitInfo.segmentDuration;
                    return (
                      <div
                        key={i}
                        className={`flex flex-col items-center px-2 py-1.5 rounded-lg text-[10px] ${
                          isLast && lastDuration < videoSplitInfo.segmentDuration
                            ? 'bg-amber-50 border border-amber-200 text-amber-700'
                            : 'bg-violet-50 border border-violet-200 text-violet-700'
                        }`}
                      >
                        <span className="font-bold">#{i + 1}</span>
                        <span>{dur}s</span>
                      </div>
                    );
                  })}
                </div>
                {segCount > STORY_MAX_SLIDES && (
                  <p className="mt-2 text-[10px] text-amber-600">
                    ⚠️ Film jest za długi — max {STORY_MAX_SLIDES} slajdów. Pozostałe {segCount - STORY_MAX_SLIDES} fragmentów zostanie pominięte.
                  </p>
                )}
              </div>
            );
          })()}

          <div className="flex gap-2">
            <button
              type="button"
              onClick={onCancelVideoSplit}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-600 bg-gray-100 border border-gray-200 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <XIcon className="w-3.5 h-3.5" />
              Anuluj
            </button>
            <button
              type="button"
              onClick={onConfirmVideoSplit}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-white bg-gradient-to-r from-violet-600 to-purple-600 rounded-lg hover:from-violet-700 hover:to-purple-700 transition-colors shadow-sm"
            >
              <Check className="w-3.5 h-3.5" />
              Podziel na slajdy
            </button>
          </div>
        </div>
      )}

      {/* Parts List */}
      {parts.length === 0 ? (
        <div className="border-2 border-dashed border-pink-200 rounded-xl p-6 text-center">
          <Layers className="w-10 h-10 text-pink-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500 mb-3">Brak slajdów — dodaj pierwszy slajd historii</p>
          <button
            type="button"
            onClick={onAddPart}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-pink-500 to-orange-500 rounded-lg hover:from-pink-600 hover:to-orange-600 transition-all"
          >
            <Plus className="w-4 h-4" />
            Dodaj pierwszy slajd
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {parts.map((part, index) => {
            const templateConfig = getSlideTemplateConfig(part.template);
            const TemplateIcon = getTemplateIcon(templateConfig.iconName);

            return (
              <div
                key={`part-${index}`}
                className={`border-2 rounded-xl p-4 transition-all group ${templateConfig.borderColor} ${templateConfig.bgColor}`}
              >
                {/* Part header */}
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <GripVertical className="w-4 h-4 text-gray-300 cursor-grab" />
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gradient-to-r from-pink-500 to-orange-500 text-white text-xs font-bold">
                      {index + 1}
                    </span>
                    <span className="text-xs text-gray-500">
                      Slajd {index + 1} z {parts.length}
                    </span>
                    {/* Template badge */}
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${templateConfig.badgeColor}`}>
                      <TemplateIcon className="w-3 h-3" />
                      {templateConfig.label}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      type="button"
                      onClick={() => onMovePart(index, index - 1)}
                      disabled={index === 0}
                      className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                      title="Przesuń w górę"
                    >
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => onMovePart(index, index + 1)}
                      disabled={index === parts.length - 1}
                      className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                      title="Przesuń w dół"
                    >
                      <ChevronDown className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => onRemovePart(index)}
                      disabled={isUploading}
                      className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                      title="Usuń slajd"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Template Selector */}
                <SlideTemplateSelector
                  template={part.template}
                  onChange={(tmpl) => onUpdatePart(index, { template: tmpl })}
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* Text content */}
                  <div>
                    <textarea
                      value={part.content}
                      onChange={(e) => onUpdatePart(index, { content: e.target.value })}
                      className={`w-full h-24 p-2.5 text-sm border rounded-lg focus:ring-2 focus:ring-pink-400 focus:border-pink-400 resize-none ${
                        part.template === 'title' ? 'font-bold text-base' : 
                        part.template === 'quote' ? 'italic border-l-4 border-l-amber-400 pl-4' :
                        part.template === 'cta' ? 'font-semibold' :
                        ''
                      }`}
                      placeholder={
                        part.template === 'title' ? `Nagłówek / Hook slajdu ${index + 1}...` :
                        part.template === 'quote' ? `Cytat / Wyróżnione zdanie...` :
                        part.template === 'cta' ? `Call-to-Action — zachęta do działania...` :
                        part.template === 'poll' ? `Pytanie + opcje A) B)...` :
                        part.template === 'media' ? `Krótki opis media (opcjonalnie)...` :
                        `Tekst slajdu ${index + 1}...`
                      }
                    />
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-xs text-gray-400">
                        {part.content.length} znaków
                      </span>
                      <div className="flex items-center gap-2">
                        <label className="text-xs text-gray-500">
                          Czas:
                          <input
                            type="number"
                            min={1}
                            max={60}
                            value={part.durationSeconds}
                            onChange={(e) => onUpdatePart(index, { durationSeconds: parseInt(e.target.value) || STORY_SLIDE_DEFAULT_DURATION })}
                            className="w-12 ml-1 px-1 py-0.5 text-xs border border-gray-200 rounded text-center"
                          />
                          s
                        </label>
                      </div>
                    </div>

                    {/* ─── Template-specific fields ───────── */}

                    {/* CTA: Link URL + Button text */}
                    {part.template === 'cta' && (
                      <div className="mt-2 space-y-1.5 p-2.5 bg-emerald-50 border border-emerald-200 rounded-lg">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-semibold text-emerald-700 uppercase tracking-wide">CTA</span>
                        </div>
                        <input
                          type="url"
                          value={part.ctaUrl || ''}
                          onChange={(e) => onUpdatePart(index, { ctaUrl: e.target.value })}
                          placeholder="https://example.com/promo"
                          className="w-full px-2.5 py-1.5 text-xs border border-emerald-200 rounded-md focus:ring-1 focus:ring-emerald-400 focus:border-emerald-400 bg-white"
                        />
                        <input
                          type="text"
                          value={part.ctaButtonText || ''}
                          onChange={(e) => onUpdatePart(index, { ctaButtonText: e.target.value })}
                          placeholder='Tekst przycisku, np. "Kup teraz" lub "Sprawdź →"'
                          className="w-full px-2.5 py-1.5 text-xs border border-emerald-200 rounded-md focus:ring-1 focus:ring-emerald-400 focus:border-emerald-400 bg-white"
                        />
                      </div>
                    )}

                    {/* Quote: Author */}
                    {part.template === 'quote' && (
                      <div className="mt-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="flex items-center gap-2 mb-1.5">
                          <span className="text-[10px] font-semibold text-amber-700 uppercase tracking-wide">Cytat</span>
                        </div>
                        <input
                          type="text"
                          value={part.quoteAuthor || ''}
                          onChange={(e) => onUpdatePart(index, { quoteAuthor: e.target.value })}
                          placeholder="— Autor cytatu"
                          className="w-full px-2.5 py-1.5 text-xs border border-amber-200 rounded-md focus:ring-1 focus:ring-amber-400 focus:border-amber-400 bg-white italic"
                        />
                      </div>
                    )}

                    {/* Poll: Option A + Option B */}
                    {part.template === 'poll' && (
                      <div className="mt-2 p-2.5 bg-violet-50 border border-violet-200 rounded-lg space-y-1.5">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-semibold text-violet-700 uppercase tracking-wide">Ankieta — opcje</span>
                        </div>
                        <div className="grid grid-cols-2 gap-1.5">
                          <input
                            type="text"
                            value={part.pollOptionA || ''}
                            onChange={(e) => onUpdatePart(index, { pollOptionA: e.target.value })}
                            placeholder="Opcja A"
                            className="px-2.5 py-1.5 text-xs border border-violet-200 rounded-md focus:ring-1 focus:ring-violet-400 focus:border-violet-400 bg-white font-medium"
                          />
                          <input
                            type="text"
                            value={part.pollOptionB || ''}
                            onChange={(e) => onUpdatePart(index, { pollOptionB: e.target.value })}
                            placeholder="Opcja B"
                            className="px-2.5 py-1.5 text-xs border border-violet-200 rounded-md focus:ring-1 focus:ring-violet-400 focus:border-violet-400 bg-white font-medium"
                          />
                        </div>
                      </div>
                    )}
                    
                    {/* Video segment duration warning */}
                    {part.mediaType === 'video' && part.startSeconds !== undefined && part.endSeconds !== undefined && (() => {
                      const segmentDuration = part.endSeconds - part.startSeconds;
                      if (segmentDuration > STORY_MAX_DURATION_SECONDS) {
                        return (
                          <div className="mt-2 px-2 py-1.5 bg-red-50 border border-red-200 rounded text-[11px] text-red-700 flex items-start gap-1.5">
                            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                            <div>
                              <span className="font-semibold">LIMIT PRZEKROCZONY:</span> Segment {segmentDuration}s przekracza limit Instagram ({STORY_MAX_DURATION_SECONDS}s). 
                              Ten slajd nie zostanie opublikowany na Instagram.
                            </div>
                          </div>
                        );
                      } else if (segmentDuration > 55) {
                        return (
                          <div className="mt-2 px-2 py-1.5 bg-amber-50 border border-amber-200 rounded text-[11px] text-amber-700 flex items-start gap-1.5">
                            <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                            <div>
                              <span className="font-semibold">UWAGA:</span> Segment {segmentDuration}s jest bliski limitu Instagram ({STORY_MAX_DURATION_SECONDS}s).
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>

                  {/* Media */}
                  <div>
                    {part.mediaUrl ? (
                      <div className="relative rounded-lg overflow-hidden bg-gray-900">
                        {part.mediaType === 'video' ? (
                          <VideoSegmentPreview
                            src={part.mediaUrl}
                            startSeconds={part.startSeconds}
                            endSeconds={part.endSeconds}
                          />
                        ) : (
                          <img src={part.mediaUrl} alt={`Slajd ${index + 1}`} className="w-full h-32 object-cover" />
                        )}
                        <button
                          type="button"
                          onClick={() => onUpdatePart(index, { mediaUrl: null, startSeconds: undefined, endSeconds: undefined })}
                          className="absolute top-1.5 right-1.5 bg-red-500/90 text-white rounded-full p-1 hover:bg-red-600 transition-colors z-10"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <label
                        className={`flex flex-col items-center justify-center h-24 border-2 border-dashed rounded-lg cursor-pointer hover:border-pink-400 hover:bg-pink-50 transition-colors ${
                          part.template === 'media' ? 'border-pink-400 bg-pink-50/50' : 'border-gray-200'
                        }`}
                      >
                        <div className="flex items-center gap-1 mb-1">
                          <ImageIcon className="w-5 h-5 text-gray-300" />
                          <span className="text-gray-200">/</span>
                          <FilmIcon className="w-5 h-5 text-gray-300" />
                        </div>
                        <span className="text-xs text-gray-400">Dodaj zdjęcie lub wideo</span>
                        <span className="text-[10px] text-gray-300">{STORY_IMAGE_RECOMMENDED_SIZE} · MP4/MOV ≤{STORY_MAX_DURATION_SECONDS}s</span>
                        <input
                          ref={(el) => setFileInputRef(index, el)}
                          type="file"
                          accept="image/*,video/*"
                          className="hidden"
                          onChange={(e) => onPartMediaChange(index, e)}
                          disabled={isUploading}
                        />
                      </label>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Info */}
      <div className="flex items-start gap-2 p-3 bg-pink-50 border border-pink-200 rounded-lg">
        <Info className="w-4 h-4 text-pink-600 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-pink-700 space-y-1">
          <p><strong>Max slajdów:</strong> {STORY_MAX_SLIDES} · <strong>Format:</strong> {STORY_IMAGE_RECOMMENDED_SIZE} (9:16)</p>
          <p><strong>Media:</strong> Zdjęcia ({STORY_SUPPORTED_IMAGE_FORMATS.join(', ').toUpperCase()}) i wideo ({STORY_SUPPORTED_VIDEO_FORMATS.join(', ').toUpperCase()}) do {STORY_MAX_DURATION_SECONDS}s per slajd</p>
          <p><strong>Auto-podział:</strong> Wpisz długi tekst w opis i kliknij „Auto-podział" — system podzieli go na slajdy automatycznie</p>
          <p><strong>AI → Slajdy:</strong> Użyj AI Asystenta — odpowiedź zostanie automatycznie rozdzielona na slajdy z wykrytym typem</p>
          <p><strong>Szablony:</strong> Każdy slajd ma typ (Tytuł, Tekst, Cytat, CTA, Ankieta, Media) — wpływa na wizualne wyróżnienie</p>
          <p><strong>Etapowa publikacja:</strong> Slajdy zostaną opublikowane w kolejności z odpowiednimi przerwami</p>
        </div>
      </div>
    </div>
  );
};
